import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import { makeStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import { useEffect, useState } from 'react'
import { Button as RaButton, FileField, FileInput, Labeled, useNotify, useRefresh } from 'react-admin'
import { Form } from 'react-final-form'

import { detectEnvironment, Environment } from 'config/config'
import { captureExceptionIfProduction, convertFileToBase64, getVGSUrl } from 'helpers/utils'
import { postRequest } from '../../api/common'
import { withDisableBackdropClick } from '../../helpers/withDisableBackdropClick'
import { hasPermission, Permissions } from '../../Permissions'

const useStyles = makeStyles({
  dialogContent: {
    minWidth: '300px',
  },
  taxId: {
    background: '#E8FBFF',
    padding: '10px 30px',
    borderRadius: '5px',
    letterSpacing: '3px',
    margin: '0 auto',
    display: 'inline-block',
    fontSize: '30px',
  },
  saveButton: {
    backgroundColor: '#002CB1',
    color: 'white',
    borderRadius: '570px',
    width: '40%',
  },
})

const TeamTaxForm = (props: any) => {
  const canChangeTeam = hasPermission(props.permissions, Permissions.ChangeTeam)
  const [taxFormDecrypted, setTaxFormDecrypted] = useState(undefined)
  const [isEditing, setIsEditing] = useState(false)

  return (
    <>
      <Box display="flex" flexDirection="row" alignItems="center">
        <Box flex={1}>
          <Labeled label="W-9">
            {taxFormDecrypted !== undefined ? (
              !taxFormDecrypted ? (
                <Typography component="span" variant="body2">
                  No tax form uploaded
                </Typography>
              ) : (
                <a download="w9" href={taxFormDecrypted}>
                  Download file
                </a>
              )
            ) : (
              <TaxFormFile {...props} onReveal={setTaxFormDecrypted}>
                Reveal
              </TaxFormFile>
            )}
          </Labeled>
        </Box>
        {canChangeTeam && taxFormDecrypted !== undefined && (
          <Box flex={3}>
            <RaButton onClick={() => setIsEditing(true)} label="Edit" />
          </Box>
        )}
      </Box>
      {isEditing && (
        <EditTaxForm {...props} taxFormDecrypted={taxFormDecrypted} handleClose={() => setIsEditing(false)} />
      )}
    </>
  )
}

const TaxFormFile = (props: any) => {
  const [reveal, setReveal] = useState(false)

  return (
    <>
      <Typography component="span" variant="body2">
        ***********
      </Typography>
      <Button variant="text" color="primary" onClick={() => setReveal(true)}>
        {props.children}
      </Button>
      {reveal && <RevealedCredentials {...props} onLoad={props.onReveal} />}
    </>
  )
}

const EditTaxForm = (props: any) => {
  const [loading, setLoading] = useState(props.taxIdDecrypted)
  const classes = useStyles()
  const notify = useNotify()
  const refresh = useRefresh()
  const url = `${getVGSUrl()}/api/v1/console/teams/${props.record.id}/add-tax-info/`

  const withCredentials = detectEnvironment() === Environment.DEVELOPMENT ? false : true

  const handleSubmit = async (values: any) => {
    setLoading(true)
    let taxFormEncrypted = values.tax_form_encrypted
    if (taxFormEncrypted !== null) {
      taxFormEncrypted = await convertFileToBase64(taxFormEncrypted.rawFile)
    } else {
      taxFormEncrypted = ''
    }

    const payload = {
      tax_form_encrypted: taxFormEncrypted,
    }

    postRequest(url, payload, { withCredentials })
      .then(() => {
        notify('Tax form changed', { type: 'success' })
        refresh()
        props.handleClose()
      })
      .catch((error) => {
        notify('Something went wrong', { type: 'error' })
        captureExceptionIfProduction(error)
        setLoading(false)
      })
  }

  const initialValues = { tax_form_encrypted: props.taxFormDecrypted }

  return (
    <Dialog open onClose={withDisableBackdropClick(props.handleClose)}>
      <DialogTitle>Edit Taxpayer W9</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <DialogContentText>Remove the current document or upload a new one</DialogContentText>

        <Form
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validate={undefined}
          render={(formProps) => {
            const formState = formProps.form.getState()
            const disableSave = loading || formState.values.tax_form_encrypted === props.taxFormDecrypted
            return (
              <>
                <FileInput source="tax_form_encrypted">
                  <FileField title="tax_form_encrypted" />
                </FileInput>
                <br />

                <Box display="flex" flexDirection="column" alignItems="center">
                  <Button
                    color="primary"
                    variant="contained"
                    type="submit"
                    className={classes.saveButton}
                    disabled={disableSave}
                    onClick={formProps.handleSubmit}
                  >
                    Save
                  </Button>
                  <Button color="secondary" onClick={props.handleClose}>
                    Cancel
                  </Button>
                </Box>
              </>
            )
          }}
        />

        <br />
      </DialogContent>
    </Dialog>
  )
}

const RevealedCredentials = ({ record, onLoad }: any) => {
  const [loading, setLoading] = useState(true)
  const [, setTaxId] = useState(null)
  const url = `${getVGSUrl()}/api/v1/console/teams/${record.id}/tax-info/reveal/`
  const withCredentials = detectEnvironment() === Environment.DEVELOPMENT ? false : true

  useEffect(() => {
    postRequest(url, null, { withCredentials }).then((data) => {
      setLoading(false)
      setTaxId(data.tax_form_encrypted)
      onLoad(data.tax_form_encrypted)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (loading) {
    return <>Loading...</>
  } else {
    return null
  }
}
export default TeamTaxForm
